import React, { useEffect, useRef, useState } from 'react';
import styles from './dataTable.module.css';

export interface DataTableColumn {
  label: string;
  key: string;
  formatter?: (value: string | number) => string;
}

export interface DataTableRow {
  [key: string]: string | number;
  id: string;
}

interface DataTableProps {
  title: string;
  subHeader?: React.ReactNode;
  columns: DataTableColumn[];
  rows: DataTableRow[];
  isLoading?: boolean;
  hasError?: boolean;
  onCellClick?: (columnKey: string, row: DataTableRow) => void;
  clickableColumns?: string[];
  monthFilter?: {
    options: string[];
    selected: string | null;
    onChange: (month: string | null) => void;
  };
  groupByKey?: string;
  groupLabelFormatter?: (value: string | number) => string;
}

const SHIMMER_ROWS = 6;

const DataTable: React.FC<DataTableProps> = ({
  title,
  subHeader,
  columns,
  rows,
  isLoading,
  hasError,
  onCellClick,
  clickableColumns = [],
  monthFilter,
  groupByKey,
  groupLabelFormatter,
}) => {
  const stickyHeaderRef = useRef<HTMLDivElement>(null);
  const [thTop, setThTop] = useState(0);

  // Keep th offset in sync with the sticky header's actual rendered height.
  // Defer observer-driven updates to the next frame so layout/state updates
  // don't run inside the ResizeObserver callback (Chromium "loop" warning).
  useEffect(() => {
    const el = stickyHeaderRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      requestAnimationFrame(() => setThTop(el.offsetHeight));
    });
    observer.observe(el);
    setThTop(el.offsetHeight);
    return () => observer.disconnect();
  }, []);

  return (
    <div className={styles.wrapper}>
      {/* Section 1: title + month pills — sticky */}
      <div className={styles.stickyHeader} ref={stickyHeaderRef}>
        <div className={styles.headerRow}>
          <p className={styles.title}>{title}</p>
          {subHeader && <div className={styles.headerActions}>{subHeader}</div>}
        </div>

        {monthFilter && monthFilter.options.length > 0 && (
          <div className={styles.monthFilterSection}>
            <span className={styles.monthFilterLabel}>Period</span>
            <div className={styles.monthFilterRow}>
              <button
                className={`${styles.monthFilterPill} ${monthFilter.selected === null ? styles.monthFilterPillActive : ''}`}
                onClick={() => monthFilter.onChange(null)}
              >
                All
              </button>
              {monthFilter.options.map(m => (
                <button
                  key={m}
                  className={`${styles.monthFilterPill} ${monthFilter.selected === m ? styles.monthFilterPillActive : ''}`}
                  onClick={() => monthFilter.onChange(m)}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              {/* Section 2: column headers — sticky, offset below the stickyHeader */}
              {columns.map((col) => (
                <th key={col.key} className={styles.th} style={{ top: thTop }}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading
              ? Array.from({ length: SHIMMER_ROWS }).map((_, rowIdx) => (
                  <tr key={rowIdx} className={styles.tr}>
                    {columns.map((col) => (
                      <td key={col.key} className={styles.td}>
                        <div className={`${styles.shimmerBlock} ${styles.shimmerCell}`} />
                      </td>
                    ))}
                  </tr>
                ))
              : hasError
              ? (
                  <tr>
                    <td colSpan={columns.length} style={{ textAlign: 'center', padding: '36px 0' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="10" stroke="#F59E0B" strokeWidth="1.5"/>
                          <path d="M12 8v4.5" stroke="#F59E0B" strokeWidth="1.5" strokeLinecap="round"/>
                          <circle cx="12" cy="16" r="0.875" fill="#F59E0B"/>
                        </svg>
                        <span style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.5 }}>
                          Data unavailable. Please try refreshing.
                        </span>
                      </div>
                    </td>
                  </tr>
                )
              : rows.length === 0
              ? (
                  <tr>
                    <td colSpan={columns.length} style={{ textAlign: 'center', padding: '36px 0' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="10" stroke="#CBD5E1" strokeWidth="1.5"/>
                          <path d="M8 12h8" stroke="#CBD5E1" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        <span style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.5 }}>
                          No data available for the selected period.
                        </span>
                      </div>
                    </td>
                  </tr>
                )
              : (() => {
                  // Cluster rows by group value before rendering so a header is
                  // never repeated if the source data isn't already contiguous
                  // by groupByKey (e.g. API rows not sorted by month/date).
                  let orderedRows = rows;
                  if (groupByKey !== undefined) {
                    const groups = new Map<string | number, DataTableRow[]>();
                    for (const row of rows) {
                      const groupValue = row[groupByKey];
                      const bucket = groups.get(groupValue);
                      if (bucket) bucket.push(row);
                      else groups.set(groupValue, [row]);
                    }
                    orderedRows = Array.from(groups.values()).flat();
                  }
                  let lastGroupValue: string | number | undefined;
                  return orderedRows.flatMap((row) => {
                    const elements: React.ReactNode[] = [];
                    if (groupByKey !== undefined) {
                      const groupValue = row[groupByKey];
                      if (groupValue !== lastGroupValue) {
                        lastGroupValue = groupValue;
                        elements.push(
                          <tr key={`group-${groupValue}`} className={styles.groupHeaderRow}>
                            <td colSpan={columns.length} className={styles.groupHeaderCell}>
                              {groupLabelFormatter ? groupLabelFormatter(groupValue) : groupValue}
                            </td>
                          </tr>
                        );
                      }
                    }
                    elements.push(
                      <tr key={row.id} className={styles.tr}>
                        {columns.map((col) => {
                          const isClickable = clickableColumns.includes(col.key) && !!onCellClick;
                          return (
                            <td
                              key={col.key}
                              className={styles.td}
                              style={isClickable ? { cursor: 'pointer' } : undefined}
                              onClick={isClickable ? () => onCellClick!(col.key, row) : undefined}
                            >
                              {isClickable ? (
                                <span className={styles.clickableCell}>{col.formatter ? col.formatter(row[col.key]) : row[col.key]}</span>
                              ) : (
                                col.formatter ? col.formatter(row[col.key]) : row[col.key]
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                    return elements;
                  });
                })()}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DataTable;
